import Big from 'big.js';
import {
    action,
    computed,
    makeObservable,
    observable,
    runInAction,
} from 'mobx';

import { STEP_PROGRESS_PERCENT, StepOrder } from 'app/constants/step';
import { LoadStateStore } from 'app/loading/LoadStateStore';
import { getActualDraftOrder } from 'app/logic/pages/draftOrder/utils';
import { CardOrder } from 'app/model/CardOrder';
import { CardOrderCancelReason } from 'app/model/CardOrderCancelReason';
import { OrderService } from 'app/service/OrderService';
import { StepNames } from 'app/StepNames';
import { getWithFirstLetterCapital } from 'app/utils/StringUtils';

export class DraftOrderPageStore {
    readonly loadStateStore = new LoadStateStore();

    readonly actionStateStore = new LoadStateStore(true);

    private readonly orderService = new OrderService();

    @observable
    private _draftOrder: CardOrder;

    @observable
    private _initialOrder: CardOrder;

    constructor() {
        makeObservable(this);
    }

    /**
     * Инициализация страницы выбороа черновика
     * @param orderId id заявки с которой пришел пользователь
     */
    @action
    async initialize(orderId: string) {
        return this.loadStateStore.loadData(async () => {
            const [order, draftOrders] = await Promise.all([
                this.orderService.getById(orderId),
                this.orderService.getDraftOrders(),
            ]);

            const actualDraftOrder = getActualDraftOrder(order, draftOrders);

            runInAction(() => {
                this.setInitialOrder(order);
                this.setDraftOrder(actualDraftOrder);
            });
        });
    }

    @action
    setInitialOrder(order: CardOrder) {
        this._initialOrder = order;
    }

    @action
    setDraftOrder(order: CardOrder) {
        this._draftOrder = order;
    }

    @computed
    get actualDraftOrder(): CardOrder {
        return this._draftOrder;
    }

    /**
     * Обращение к клиенту перед текстом обращения
     */
    @computed
    get appeal(): string {
        const name: string | null =
            this._initialOrder?.client.name ||
            this.actualDraftOrder?.client.name ||
            null;

        if (!name) {
            return '';
        }

        return getWithFirstLetterCapital(name);
    }

    /**
     * Возвращает ступень заполнения заявки в формате ключа StepNames
     */
    @computed
    get orderStep(): keyof typeof StepNames {
        const indexOfS = Object.values(StepNames).indexOf(
            this.actualDraftOrder?.stage,
        );

        return Object.keys(StepNames)[indexOfS] as keyof typeof StepNames;
    }

    /**
     * Возвращает номер ступени заполнения заявки
     */
    @computed
    get orderStageNumber(): number {
        return StepOrder[this.orderStep] || 2;
    }

    /**
     * Рассчитывает процент прироста к одобрению согласно спецификации
     */
    @computed
    get calculatedProgress(): Big {
        return Big((this.orderStageNumber - 1) * STEP_PROGRESS_PERCENT);
    }

    @action
    cancelInitialOrder = async () =>
        this.actionStateStore.loadData(async () => {
            return this.orderService.cancelOrder(
                this._initialOrder.id,
                CardOrderCancelReason.PROCEED_CREDIT_CARD_ORDER,
            );
        });
}
